import React, { useState, useEffect } from 'react';
import { getCustomers, updateCustomer, addCustomer } from '../firebase/database';
import { Modal } from './DriversPage';

const EMPTY_FORM = {
  name: '', email: '', phone: '', homeAddress: '',
  homeLatitude: '', homeLongitude: '', notes: ''
};

export default function CustomersPage() {
  const [customers, setCustomers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [selected, setSelected]     = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab]               = useState('all'); // 'all' | 'admin_created' | 'pending' | 'approved'

  const load = () => {
    setLoading(true);
    getCustomers().then(data => {
      setCustomers(data);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const filtered = customers
    .filter(c => {
      if (tab === 'admin_created') return c.createdByAdmin === true;
      if (tab === 'pending')       return !c.status || c.status === 'pending';
      if (tab === 'approved')      return c.status === 'approved';
      return true;
    })
    .filter(c =>
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase()) ||
      c.phone?.includes(search)
    );

  const pendingCount = customers.filter(c => !c.status || c.status === 'pending').length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ fontSize: 13, color: '#94a3b8' }}>{customers.length} registered customers</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input
            placeholder="Search by name, email, phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '9px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', width: 240 }}
          />
          <button
            onClick={() => setShowCreate(true)}
            style={{
              padding: '9px 18px', background: '#f59e0b', border: 'none',
              borderRadius: 8, color: '#0f172a', fontWeight: 700, fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6
            }}
          >
            + Create Customer
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #e2e8f0', paddingBottom: 0 }}>
        {[
          { key: 'all',           label: 'All' },
          { key: 'admin_created', label: 'Admin Created' },
          { key: 'approved',      label: 'Approved' },
          { key: 'pending',       label: `Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 16px', border: 'none', background: 'transparent',
            borderBottom: tab === t.key ? '2px solid #f59e0b' : '2px solid transparent',
            color: tab === t.key ? '#f59e0b' : '#64748b',
            fontWeight: tab === t.key ? 700 : 400, fontSize: 13, cursor: 'pointer',
            marginBottom: -1
          }}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Loading customers...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {filtered.length === 0 && (
            <div style={{
              gridColumn: '1/-1', background: '#fff', borderRadius: 12,
              border: '1px solid #e2e8f0', padding: '48px 20px', textAlign: 'center', color: '#94a3b8'
            }}>
              {search ? 'No customers match your search' : 'No customers in this category'}
            </div>
          )}
          {filtered.map(customer => (
            <CustomerCard
              key={customer.id}
              customer={customer}
              onView={() => setSelected(customer)}
              onApprove={async () => {
                await updateCustomer(customer.id, { status: 'approved' });
                load();
              }}
            />
          ))}
        </div>
      )}

      {/* View Detail Modal */}
      {selected && (
        <CustomerDetailModal
          customer={selected}
          onClose={() => setSelected(null)}
          onApprove={async () => {
            await updateCustomer(selected.id, { status: 'approved' });
            setSelected(null);
            load();
          }}
          onReject={async () => {
            await updateCustomer(selected.id, { status: 'rejected' });
            setSelected(null);
            load();
          }}
        />
      )}

      {/* Create Customer Modal */}
      {showCreate && (
        <CreateCustomerModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

// ── Customer Card ──────────────────────────────────────────
function CustomerCard({ customer, onView, onApprove }) {
  const isAdminCreated = customer.createdByAdmin === true;
  const isPending = !customer.status || customer.status === 'pending';

  return (
    <div style={{
      background: '#fff', borderRadius: 12,
      border: isPending ? '1px solid #fde68a' : '1px solid #e2e8f0',
      padding: 18, cursor: 'pointer', transition: 'all 0.15s', position: 'relative'
    }}
    onClick={onView}
    onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
    onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}
    >
      {isAdminCreated && (
        <div style={{
          position: 'absolute', top: 10, right: 10,
          background: '#dbeafe', color: '#1e40af',
          fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10
        }}>Admin Created</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 42, height: 42, borderRadius: '50%', background: isAdminCreated ? '#dbeafe' : '#fef3c7',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 16, color: isAdminCreated ? '#1e40af' : '#92400e'
        }}>
          {customer.name?.charAt(0)?.toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>{customer.name}</div>
          <div style={{ fontSize: 12, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.email || 'No email'}</div>
        </div>
      </div>
      <div style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>📱 {customer.phone || '—'}</div>
      {customer.homeAddress && (
        <div style={{ fontSize: 12, color: '#94a3b8' }}>
          🏠 {customer.homeAddress?.substring(0, 40)}{customer.homeAddress?.length > 40 ? '...' : ''}
        </div>
      )}
      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{
          padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
          background: customer.status === 'approved' ? '#dcfce7' : customer.status === 'rejected' ? '#fee2e2' : '#fef9c3',
          color: customer.status === 'approved' ? '#166534' : customer.status === 'rejected' ? '#991b1b' : '#854d0e'
        }}>
          {customer.status === 'approved' ? 'Approved' : customer.status === 'rejected' ? 'Rejected' : 'Pending'}
        </span>
        {isPending && (
          <button
            onClick={e => { e.stopPropagation(); onApprove(); }}
            style={{
              padding: '4px 12px', background: '#22c55e', border: 'none',
              borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer'
            }}
          >Approve</button>
        )}
      </div>
    </div>
  );
}

// ── Customer Detail Modal ──────────────────────────────────
function CustomerDetailModal({ customer, onClose, onApprove, onReject }) {
  const isPending = !customer.status || customer.status === 'pending';
  return (
    <Modal title="Customer Details" onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <div style={{
          width: 52, height: 52, borderRadius: '50%',
          background: customer.createdByAdmin ? '#dbeafe' : '#fef3c7',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 20, color: customer.createdByAdmin ? '#1e40af' : '#92400e'
        }}>
          {customer.name?.charAt(0)?.toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{customer.name}</div>
          <div style={{ fontSize: 13, color: '#64748b' }}>
            {customer.createdByAdmin ? '🔧 Admin Created Account' : '📱 App Registration'}
          </div>
        </div>
      </div>

      {[
        ['Email',        customer.email || '—'],
        ['Phone',        customer.phone || '—'],
        ['Home Address', customer.homeAddress || '—'],
        ['Status',       customer.status || 'pending'],
        ['Notes',        customer.notes || '—'],
        ['Registered',   customer.createdAt?.toDate?.()?.toLocaleDateString('en-US') || 'N/A'],
      ].map(([label, value]) => (
        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f1f5f9', fontSize: 14 }}>
          <span style={{ color: '#64748b', fontWeight: 500 }}>{label}</span>
          <span style={{ color: '#0f172a', fontWeight: 600, maxWidth: '60%', textAlign: 'right', textTransform: 'capitalize' }}>{value}</span>
        </div>
      ))}

      {isPending && (
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button onClick={onReject} style={{
            flex: 1, padding: '10px', background: '#fee2e2', border: '1px solid #fca5a5',
            borderRadius: 8, color: '#991b1b', fontWeight: 600, cursor: 'pointer', fontSize: 14
          }}>✕ Reject</button>
          <button onClick={onApprove} style={{
            flex: 1, padding: '10px', background: '#22c55e', border: 'none',
            borderRadius: 8, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14
          }}>✓ Approve</button>
        </div>
      )}

      {customer.status === 'approved' && (
        <div style={{ marginTop: 16 }}>
          <a
            href={`#/rides`}
            onClick={onClose}
            style={{
              display: 'block', textAlign: 'center', padding: '10px',
              background: '#f59e0b', borderRadius: 8, color: '#0f172a',
              fontWeight: 700, fontSize: 14, textDecoration: 'none'
            }}
          >🚗 Create Ride for This Customer</a>
        </div>
      )}
    </Modal>
  );
}

// ── Create Customer Modal ──────────────────────────────────
function CreateCustomerModal({ onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      setError('Name and phone number are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Generate a unique ID for admin-created customers (no Firebase Auth account needed)
      const uid = 'admin_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
      await addCustomer(uid, {
        uid,
        name:           form.name.trim(),
        email:          form.email.trim() || '',
        phone:          form.phone.trim(),
        homeAddress:    form.homeAddress.trim(),
        homeLatitude:   parseFloat(form.homeLatitude) || null,
        homeLongitude:  parseFloat(form.homeLongitude) || null,
        notes:          form.notes.trim(),
        status:         'approved',       // admin-created = auto approved
        createdByAdmin: true,
        fcmToken:       '',
      });
      onCreated();
    } catch (err) {
      setError(err.message || 'Failed to create customer.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Create Customer Account" onClose={onClose}>
      {/* Info banner */}
      <div style={{
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
        padding: '10px 14px', fontSize: 13, color: '#1e40af', marginBottom: 20, lineHeight: 1.5
      }}>
        ℹ️ This creates an account for customers who can't use the app. The account is auto-approved and you can immediately create rides for them.
      </div>

      <CField label="Full Name *"    value={form.name}        onChange={v => set('name', v)} />
      <CField label="Phone Number *" value={form.phone}       onChange={v => set('phone', v)} placeholder="(555) 555-5555" />
      <CField label="Email"          value={form.email}       onChange={v => set('email', v)} type="email" placeholder="Optional" />
      <CField label="Home Address"   value={form.homeAddress} onChange={v => set('homeAddress', v)} placeholder="Full street address" />

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <CField label="Latitude (optional)"  value={form.homeLatitude}  onChange={v => set('homeLatitude', v)}  placeholder="41.8781" />
        </div>
        <div style={{ flex: 1 }}>
          <CField label="Longitude (optional)" value={form.homeLongitude} onChange={v => set('homeLongitude', v)} placeholder="-87.6298" />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 6, fontWeight: 500 }}>Notes (internal)</label>
        <textarea
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          placeholder="Any notes about this customer..."
          rows={3}
          style={{
            width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
            borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
            resize: 'vertical', fontFamily: 'inherit'
          }}
        />
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{
          padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: 8,
          background: '#fff', cursor: 'pointer', fontSize: 14
        }}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{
          padding: '10px 20px', background: '#f59e0b', border: 'none',
          borderRadius: 8, fontWeight: 700, fontSize: 14,
          cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1
        }}>
          {saving ? 'Creating...' : '✓ Create Customer'}
        </button>
      </div>
    </Modal>
  );
}

function CField({ label, value, onChange, type = 'text', placeholder, required }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 6, fontWeight: 500 }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        style={{
          width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
          borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box'
        }}
      />
    </div>
  );
}
